import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';
import type { Options } from 'nuqs';
import { useTransition, useEffect, useState, useCallback, useRef } from 'react';

interface DataTableSearchProps {
  searchKey?: string;
  searchQuery: string;
  className?: string;
  setSearchQuery: (
    value: string | ((old: string) => string | null) | null,
    options?: Options | undefined
  ) => Promise<URLSearchParams>;
  setPage: (
    value: number | ((old: number) => number | null) | null,
    options?: Options | undefined
  ) => Promise<URLSearchParams>;
}

export function DataTableSearch({
  searchKey,
  searchQuery,
  setSearchQuery,
  className
}: DataTableSearchProps) {
  const [isLoading, startTransition] = useTransition();
  const [localValue, setLocalValue] = useState(searchQuery || '');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state with external searchQuery
  useEffect(() => {
    setLocalValue(searchQuery || '');
  }, [searchQuery]);

  // Debounce function - optimized to handle null/undefined values
  const debouncedSearch = useCallback(
    (value: string) => {
      startTransition(() => {
        setSearchQuery(value || null);
        // setPage(1);
      });
    },
    [setSearchQuery]
  );

  // Debounced search effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      debouncedSearch(localValue);
    }, 500); // Reduced debounce time for better UX

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [localValue, debouncedSearch]);

  return (
    <div className='relative flex w-fit items-center'>
      <Search className='absolute left-3 z-10 h-4 w-4 text-muted-foreground' />
      <Input
        placeholder={`Search ${searchKey || 'items'}...`}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className={cn(
          'h-10 w-full pl-9 md:max-w-sm',
          isLoading && 'animate-pulse',
          className
        )}
      />
    </div>
  );
}
